/* IgelkottPlates shared-collection page — Eric 2026-05-11 (rev144).
 *
 * Sequence:
 *   1. Parse ?t=<token> från URL.
 *   2. GET /apps/igelkott/collections/shared/<token> → snapshot + cover.
 *   3. Visa loading → content (cover, name, plate-count).
 *   4. Beslut baserat på state:
 *        - Anonym + ?autosave=1 → kontextuellt fel (autosave kräver login,
 *          troligt orsak: login-redirect tappade param). Visa Save-CTA.
 *        - Anonym: rendera [Sign in to save] + [Just view] buttons.
 *        - Inloggad: POST /collections/import för att kolla idempotens.
 *            Om action: "noop" → already imported → redirect till
 *              /?collection=<existing-id>
 *            Annars: rendera [Save to my account] + [Just view] buttons.
 *        - Inloggad + ?autosave=1 → kör save automatiskt utan modal.
 *
 *   5. "Save" → POST /collections/import → redirect /?collection=<new-id>
 *   6. "Just view" → redirect /?shared=<token>
 *   7. "Sign in to save" (anonym) → /customer_authentication/login?return_to=
 *      /pages/collection?t=<token>&autosave=1
 *
 * Notice-rendering: server-svaret kan returnera fewer plates än ursprungligt
 * lagrade (eftersom share-tid eller efter; vid resolve ignorerar borttagna
 * plates). Frontend kan jämföra mot share-token-payload om vi vill visa
 * "N plates no longer available" — för MVP visar vi bara final count.
 */
(function () {
  'use strict';

  const log = (window.igelkott && window.igelkott.log) || {
    debug: () => {}, info: () => {},
    warn: console.warn.bind(console), error: console.error.bind(console),
  };
  const fetchAuth =
    (window.igelkott && window.igelkott.fetch) || window.fetch.bind(window);

  const root = document.querySelector('[data-igelkott-shared-collection]');
  if (!root) return;

  const isLoggedIn = root.dataset.customerLoggedIn === 'true';
  const loginUrl = root.dataset.loginUrl || '/customer_authentication/login';

  const params = new URLSearchParams(window.location.search);
  const token = (params.get('t') || '').trim();
  const autosave = params.get('autosave') === '1';

  const loadingEl = root.querySelector('[data-shared-loading]');
  const errorEl = root.querySelector('[data-shared-error]');
  const errorMsg = root.querySelector('[data-shared-error-msg]');
  const contentEl = root.querySelector('[data-shared-content]');
  const coverEl = root.querySelector('[data-shared-cover]');
  const nameEl = root.querySelector('[data-shared-name]');
  const plateCountEl = root.querySelector('[data-shared-plate-count]');
  const createdAtEl = root.querySelector('[data-shared-created-at]');
  const actionsEl = root.querySelector('.igelkott-shared-collection__actions');
  const noticeEl = root.querySelector('[data-shared-notice]');

  function showError(msg) {
    if (loadingEl) loadingEl.hidden = true;
    if (contentEl) contentEl.hidden = true;
    if (errorEl) {
      errorEl.hidden = false;
      if (errorMsg && msg) errorMsg.textContent = msg;
    }
  }

  function fmtRelative(iso) {
    if (!iso) return 'recently';
    try {
      const d = new Date(iso);
      const diff = (Date.now() - d.getTime()) / 1000;
      if (diff < 60) return 'just now';
      if (diff < 3600) return Math.floor(diff / 60) + ' min ago';
      if (diff < 86400) return Math.floor(diff / 3600) + ' h ago';
      if (diff < 86400 * 7) return Math.floor(diff / 86400) + ' d ago';
      return d.toLocaleDateString();
    } catch (_) {
      return 'recently';
    }
  }

  function makeBtn(text, variant, onClick) {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className =
      'igelkott-shared-collection__btn igelkott-shared-collection__btn--' + variant;
    btn.textContent = text;
    btn.addEventListener('click', onClick);
    return btn;
  }

  function renderContent(snapshot) {
    if (loadingEl) loadingEl.hidden = true;
    if (errorEl) errorEl.hidden = true;
    if (contentEl) contentEl.hidden = false;
    if (nameEl) nameEl.textContent = snapshot.name || 'Untitled';
    if (plateCountEl) plateCountEl.textContent = String((snapshot.plates || []).length);
    if (createdAtEl) createdAtEl.textContent = fmtRelative(snapshot.created_at);
    if (coverEl) {
      coverEl.innerHTML = '';
      if (snapshot.cover_image_url) {
        const img = document.createElement('img');
        img.src = snapshot.cover_image_url;
        img.alt = snapshot.name || '';
        img.loading = 'eager';
        coverEl.appendChild(img);
      } else {
        coverEl.innerHTML =
          '<svg viewBox="0 0 24 24" width="56" height="56" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"/></svg>';
      }
    }
  }

  function renderActions(opts) {
    if (!actionsEl) return;
    actionsEl.innerHTML = '';
    actionsEl.appendChild(opts.primary);
    actionsEl.appendChild(opts.secondary);
  }

  function showNotice(html) {
    if (!noticeEl) return;
    noticeEl.innerHTML = html;
    noticeEl.hidden = false;
  }

  function goToFilter(shared = false, collectionId = null) {
    if (shared) {
      window.location.href = `/?shared=${encodeURIComponent(token)}`;
    } else if (collectionId) {
      window.location.href = `/?collection=${encodeURIComponent(collectionId)}`;
    } else {
      window.location.href = '/';
    }
  }

  async function importCollection() {
    try {
      const r = await fetchAuth('/apps/igelkott/collections/import', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ token }),
      });
      if (!r.ok) {
        const text = await r.text().catch(() => '');
        log.error('shared.import_failed', { status: r.status, body: text.slice(0, 200) });
        showNotice(
          '<strong>Save failed.</strong> Please try again or contact sales@igelkottplates.com.'
        );
        return null;
      }
      const j = await r.json();
      log.info('shared.imported', { action: j.action, collection_id: j.collection_id });
      return j;
    } catch (err) {
      log.error('shared.import_network', { error: err.message });
      showNotice('<strong>Network error.</strong> Please try again.');
      return null;
    }
  }

  /*
    Eric 2026-05-11 (rev148) — token-match-check LOKALT istället för
    POST /import. Föregående version postade /import direkt → server
    skapade copy (sin sida av "idempotens" — om token saknas i metafield,
    skapa). Men det gjorde att första-gångs-besök ALWAYS importerade utan
    att fråga, mot Eric:s spec ("fråga först").

    Ny approach: vänta tills collections-state är synced från servern
    (window.igelkott.collections.getCollections() returnerar customer:s
    full state efter rev139-pull). Kolla om någon har imported_from_token
    matchar denna token. Om ja → redirect; om nej → visa modal med
    Save-CTA, save:ar bara om user klickar.
  */
  async function findLocallyImported() {
    const api = window.igelkott && window.igelkott.collections;
    if (!api || api.isAnonymous) return null;

    // Server-sync (rev139) körs vid mount. Vänta in om inte klar.
    // getCollections() returnerar in-memory state. Token-match poll
    // upp till ~3s för att hinna med pullServerSync.
    const deadline = Date.now() + 3000;
    while (Date.now() < deadline) {
      const all = api.getCollections();
      const match = all.find((c) => c.imported_from_token === token);
      if (match) return match;
      // Server-sync kan ha returnerat tom array (ny customer) → ge
      // pull-flowen ~200ms mellan polls
      await new Promise((r) => setTimeout(r, 200));
    }
    return null;
  }

  async function init() {
    if (!token) {
      showError('No share token provided.');
      return;
    }

    // Step 1: Fetch snapshot
    let snapshot;
    try {
      const r = await fetchAuth(
        `/apps/igelkott/collections/shared/${encodeURIComponent(token)}`,
        { method: 'GET', credentials: 'same-origin' }
      );
      if (r.status === 404) {
        showError('This share link is invalid or has been revoked.');
        return;
      }
      if (!r.ok) {
        showError('Could not load collection. Please try again.');
        return;
      }
      snapshot = await r.json();
    } catch (err) {
      log.error('shared.fetch_failed', { error: err.message });
      showError('Network error. Please try again.');
      return;
    }

    renderContent(snapshot);

    // Step 2: decide flow
    if (isLoggedIn) {
      if (autosave) {
        // Kom hit efter login-redirect; kör save automatiskt
        showNotice('Saving to your account…');
        const result = await importCollection();
        if (result && result.collection_id) {
          goToFilter(false, result.collection_id);
        }
        return;
      }

      // Logged-in: kolla token-match LOKALT (inte POST /import).
      // Om en collection redan har imported_from_token == token →
      // redirect direkt (skip modal). Annars → visa Save/View-modal,
      // import sker bara om user klickar Save.
      const existing = await findLocallyImported();
      if (existing) {
        log.info('shared.already_imported_redirect', { id: existing.id });
        goToFilter(false, existing.id);
        return;
      }
      // Inte imported → visa modal med val
      const saveBtn = makeBtn('Save to my account', 'primary', async () => {
        saveBtn.disabled = true;
        saveBtn.textContent = 'Saving…';
        const result = await importCollection();
        if (result && result.collection_id) {
          goToFilter(false, result.collection_id);
        } else {
          saveBtn.disabled = false;
          saveBtn.textContent = 'Save to my account';
        }
      });
      const viewBtn = makeBtn('Just view', 'ghost', () => goToFilter(true));
      renderActions({ primary: saveBtn, secondary: viewBtn });
    } else {
      // Anonym
      const signInUrl =
        loginUrl +
        '?return_to=' +
        encodeURIComponent(`/pages/collection?t=${token}&autosave=1`);
      const signInBtn = makeBtn('Sign in to save', 'primary', () => {
        window.location.href = signInUrl;
      });
      const viewBtn = makeBtn('Just view', 'ghost', () => goToFilter(true));
      renderActions({ primary: signInBtn, secondary: viewBtn });
    }
  }

  init();
})();
